import React, { useMemo, useState } from 'react';
import { Button, Input, Space, Table, Tag, Popconfirm, Form, Select, Upload, message } from 'antd';
import { DeleteOutlined, PlusOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import api from '../../services/api';
import customerApi from '../../services/customerApi';

export interface MsicCodeData {
  id?: number;
  code: string;
  description: string;
  _isDeletedLocal?: boolean;
  _fromDeleteRequest?: boolean;
  _isNewFromRequest?: boolean;
  _addRequest?: any;
  _deleteRequest?: any;
  _crIndex?: number;
}

interface NatureTabProps {
  isEdit: boolean;
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  form: any;
  msicCodes: MsicCodeData[];
  deletedMsicCodes?: Array<{ index: number; data: any }>;
  changeRequests?: Array<{ field_name: string; status: string; old_value?: string; new_value?: string; id: number }>;
  supportingFiles?: {
    trademark_logo?: UploadFile[];
    industry_specific_license?: UploadFile[];
  };
  onFileChange?: (field: string, files: UploadFile[]) => void;
  businessId?: number;
  latestRequest?: any;
  addMsicCode: (msicData?: MsicCodeData) => void;
  removeMsicCode: (index: number) => void;
  updateMsicCode: (index: number, field: 'code' | 'description', value: string) => void;
  getMsicCodeDisplayValue?: (index: number, field: 'code' | 'description') => string;
  handleMsicCodeFieldChange?: (index: number, field: 'code' | 'description', value: string) => void;
  handleApproveChangeRequest?: (requestId: number) => void;
  handleRejectChangeRequest?: (requestId: number) => void;
  handleApproveMsicCodeDelete?: (requestId: number) => void;
  handleRejectMsicCodeDelete?: (requestId: number) => void;
}

export const BusinessNatureTab: React.FC<NatureTabProps> = ({
  isEdit,
  isEditing = false,
  isCustomerPortal = false,
  form,
  msicCodes,
  deletedMsicCodes = [],
  changeRequests = [],
  supportingFiles = {},
  onFileChange,
  businessId,
  latestRequest,
  addMsicCode,
  removeMsicCode,
  updateMsicCode,
  getMsicCodeDisplayValue,
  handleMsicCodeFieldChange,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
  handleApproveMsicCodeDelete,
  handleRejectMsicCodeDelete,
}) => {
  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = !isEdit || isEditing;
  const maxMsicCodes = 5;
  const [fileActionLoading, setFileActionLoading] = useState<Record<string, { view?: boolean }>>({});
  const getRequestedValue = (fieldName: string): any => {
    if (!latestRequest) return null;
    return (latestRequest as any)[fieldName] ?? null;
  };

  const renderRequestedText = (value: string | null) => {
    if (!value) return null;
    return (
      <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
        Requested: {value}
      </div>
    );
  };

  const setFileLoading = (field: string, action: 'view', isLoading: boolean) => {
    setFileActionLoading((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [action]: isLoading,
      },
    }));
  };

  const handleView = async (field: string, file: UploadFile) => {
    if (!file) return;
    if (file.originFileObj) {
      const blobUrl = URL.createObjectURL(file.originFileObj);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      return;
    }
    if (!businessId) return;
    try {
      setFileLoading(field, 'view', true);
      const isCustomerPortalPath = window.location.pathname.startsWith('/customer');
      const url = isCustomerPortalPath
        ? `/customer/businesses/${businessId}/supporting-files/${field}/view`
        : `/businesses/${businessId}/supporting-files/${field}/view`;
      const apiClient = isCustomerPortalPath ? customerApi : api;
      const response = await apiClient.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      }));
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      message.error('Failed to view file');
    } finally {
      setFileLoading(field, 'view', false);
    }
  };

  const handleViewRequested = async (field: 'trademark_logo' | 'industry_specific_license') => {
    if (!businessId) return;
    try {
      setFileLoading(`request_${field}`, 'view', true);
      const response = await api.get(`/business-requests/${businessId}/nature/${field}/view`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      }));
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      message.error('Failed to view requested file');
    } finally {
      setFileLoading(`request_${field}`, 'view', false);
    }
  };

  const renderAttachmentCell = (field: 'trademark_logo' | 'industry_specific_license') => {
    const files = supportingFiles[field] || [];
    const hasFile = files.length > 0;
    const requestedAttachmentField = field === 'trademark_logo'
      ? 'trademark_logo_attachment'
      : 'industry_specific_license_attachment';
    const hasRequestedFile = !!getRequestedValue(requestedAttachmentField);
    return (
      <Space>
        {canEdit && (
          <Upload
            fileList={files}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={(info) => {
              if (onFileChange) {
                onFileChange(field, info.fileList);
              }
            }}
            maxCount={1}
            accept=".update,.png,.jpg,.jpeg"
          >
            <Button size="small" icon={<UploadOutlined />} style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }} />
          </Upload>
        )}
        {files.length > 0 && (
          <>
            <Button
              size="small"
              icon={<EyeOutlined />}
              loading={fileActionLoading[field]?.view}
              onClick={() => handleView(field, files[0])}
              style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
            />
          </>
        )}
        {hasRequestedFile && (
          <Button
            size="small"
            icon={<EyeOutlined style={{ color: '#faad14' }} />}
            loading={fileActionLoading[`request_${field}`]?.view}
            onClick={() => handleViewRequested(field)}
            style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
          />
        )}
        {canEdit && hasFile && (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
            onClick={() => {
              if (onFileChange) {
                onFileChange(field, []);
              }
            }}
          />
        )}
      </Space>
    );
  };

  // Get change request for a specific field
  const getFieldChangeRequest = (index: number, field: 'code' | 'description', msicId?: number) => {
    return changeRequests.find(cr => {
      if (cr.status !== 'pending') return false;
      // Match pattern: msic_{index}_{field}
      const pattern = `msic_${index}_${field}`;
      return cr.field_name === pattern;
    });
  };

  // Get delete change requests
  const getDeleteChangeRequest = (index: number) => {
    return changeRequests.find(cr => 
      cr.field_name === `msic_${index}_delete` && cr.status === 'pending'
    );
  };

  // Check if an MSIC code is marked for deletion
  const isMarkedForDeletion = (index: number) => {
    return deletedMsicCodes.some(d => d.index === index) ||
           getDeleteChangeRequest(index) !== undefined;
  };

  // Process MSIC codes with change requests
  const processedMsicCodes = useMemo(() => {
    // Create a set to track which MSIC codes (by code + description) are deleted for more accurate matching
    const deletedMsicKeys = new Set<string>();
    deletedMsicCodes.forEach(deleted => {
      if (deleted.data) {
        const key = `${deleted.data.code || ''}_${deleted.data.description || ''}`;
        deletedMsicKeys.add(key);
      }
    });
    
    // Reference: FamilyTab.tsx approach - start with all items, then add deleted ones back using splice
    // Start with all current MSIC codes, preserving their current indices as _crIndex
    // But we need to calculate the original indices correctly
    const allMsicCodes: Array<any> = [];
    
    // Build a map of original positions
    // First, add all current msicCodes with their calculated original indices
    // Also save the actual index in msicCodes array for correct field change handling
    msicCodes.forEach((msic, currentIdx) => {
      const msicKey = `${msic.code || ''}_${msic.description || ''}`;
      // Skip if this MSIC code is marked for deletion
      if (!deletedMsicKeys.has(msicKey)) {
        // Calculate original index: current index + number of deleted items before this position
        let originalIdx = currentIdx;
        deletedMsicCodes.forEach(deleted => {
          if (deleted.index <= currentIdx) {
            originalIdx++;
          }
        });
        // Save both original index (_crIndex) and actual index in msicCodes array (_actualIndex)
        allMsicCodes.push({ ...msic, _crIndex: originalIdx, _actualIndex: currentIdx });
      }
    });

    // Sort by _crIndex to ensure correct order before inserting deleted items
    allMsicCodes.sort((a, b) => a._crIndex - b._crIndex);

    // Add deleted MSIC codes back at their original positions using splice (like FamilyTab)
    // Process deleted items in reverse order of index to avoid index shifting issues
    const sortedDeleted = [...deletedMsicCodes].sort((a, b) => b.index - a.index);
    sortedDeleted.forEach(deleted => {
      if (deleted.data) {
        // Check if this MSIC code is already in allMsicCodes by comparing code+description
        const deletedKey = `${deleted.data.code || ''}_${deleted.data.description || ''}`;
        const exists = allMsicCodes.some(msic => {
          const msicKey = `${msic.code || ''}_${msic.description || ''}`;
          return msicKey === deletedKey;
        });
        if (!exists) {
          // Insert at the original index position using splice (like FamilyTab)
          allMsicCodes.splice(deleted.index, 0, { ...deleted.data, _crIndex: deleted.index, _isDeletedLocal: true });
        }
      }
    });
    
    // Debug log (can be removed later)
    // console.log('NatureTab: msicCodes=', msicCodes.length, 'deleted=', deletedMsicCodes.length, 'result=', allMsicCodes.length, allMsicCodes.map(m => ({code: m.code, _crIndex: m._crIndex, isDeleted: m._isDeletedLocal, isNewFromRequest: m._isNewFromRequest})));

    // Add new MSIC codes from change requests (pending add requests)
    // Handle both msic_add (array of new MSIC codes) and msic_{index}_add (single MSIC code) formats
    // Track add request index for proper ordering
    let addRequestIndex = 0;
    changeRequests.forEach(cr => {
      if (cr.status === 'pending' && cr.field_name === 'msic_add') {
        // Handle msic_add format: contains a single MSIC code object (not an array)
        try {
          const newMsic = JSON.parse(cr.new_value || '{}');
          const newMsicKey = `${newMsic.code || ''}_${newMsic.description || ''}`;
          
          // Skip if empty
          if (!newMsicKey.trim()) {
            return;
          }
          
          // Check if this MSIC code already exists in allMsicCodes (already processed)
          const existsInAllMsicCodes = allMsicCodes.some(msic => {
            const msicKey = `${msic.code || ''}_${msic.description || ''}`;
            return msicKey === newMsicKey && msicKey.trim() !== ''; // Only match if not empty
          });
          
          // Only add if it doesn't exist in allMsicCodes (to avoid duplicates)
          if (!existsInAllMsicCodes) {
            // Use a high index to ensure these appear at the end
            const placeholder = { ...newMsic, _isNewFromRequest: true, _addRequest: cr, _crIndex: 999 + addRequestIndex };
            // Always append add-request rows to the end
            allMsicCodes.push(placeholder as any);
            addRequestIndex++;
          }
        } catch (e) {
          console.error('NatureTab: Error parsing msic_add request:', e);
        }
      } else if (cr.status === 'pending' && cr.field_name.endsWith('_add')) {
        // Handle legacy msic_{index}_add format (for backward compatibility)
        const parts = cr.field_name.split('_');
        if (parts.length >= 3 && parts[0] === 'msic') {
          const index = parseInt(parts[1], 10);
          try {
            const newMsic = JSON.parse(cr.new_value || '{}');
            const newMsicKey = `${newMsic.code || ''}_${newMsic.description || ''}`;
            
            // Skip if empty
            if (!newMsicKey.trim()) {
              return;
            }
            
            // Check if this MSIC code already exists in allMsicCodes (already processed)
            const existsInAllMsicCodes = allMsicCodes.some(msic => {
              const msicKey = `${msic.code || ''}_${msic.description || ''}`;
              return msicKey === newMsicKey && msicKey.trim() !== ''; // Only match if not empty
            });
            
            // Only add if it doesn't exist in allMsicCodes (to avoid duplicates)
            if (!existsInAllMsicCodes) {
              const placeholder = { ...newMsic, _isNewFromRequest: true, _addRequest: cr, _crIndex: index };
              // Always append add-request rows to the end
              allMsicCodes.push(placeholder as any);
    }
          } catch (e) {
            console.error('NatureTab: Error parsing msic_{index}_add request:', e);
          }
        }
      }
    });

    const msicData = allMsicCodes.map((msic, idx) => {
      const crIdx = msic._crIndex !== undefined ? msic._crIndex : idx;
      // Check if this specific MSIC code (by code+description) is marked for deletion
      const msicCode = msic.code || '';
      const msicDescription = msic.description || '';
      const isDeleted = msic._isDeletedLocal || 
                       deletedMsicCodes.some(d => {
                         if (d.index === crIdx && d.data) {
                           const deletedKey = `${d.data.code || ''}_${d.data.description || ''}`;
                           const msicKey = `${msicCode}_${msicDescription}`;
                           return deletedKey === msicKey;
                         }
                         return false;
                       }) ||
                       getDeleteChangeRequest(crIdx) !== undefined;
      const deleteReq = getDeleteChangeRequest(crIdx);
      return {
        ...msic,
        key: `msic-${crIdx}-${idx}-${msic.id || 'new'}-${msic._isNewFromRequest ? 'new-request' : ''}`,
        originalIndex: crIdx,
        actualIndex: msic._actualIndex !== undefined ? msic._actualIndex : undefined, // Save actual index in msicCodes array
        isDeleted: isDeleted,
        deleteRequest: deleteReq,
        isNewFromRequest: msic._isNewFromRequest || false,
        addRequest: msic._addRequest,
      };
    });

    // Sort MSIC codes by originalIndex to maintain original order
    msicData.sort((a: any, b: any) => {
      const idxA = a.originalIndex;
      const idxB = b.originalIndex;
      return idxA - idxB;
    });

    return msicData;
  }, [msicCodes, deletedMsicCodes, changeRequests]);

  const hasPendingFieldChange = (index: number) => {
    return changeRequests.some(
      (cr) =>
        cr.status === 'pending' &&
        cr.field_name.startsWith(`msic_${index}_`) &&
        !cr.field_name.endsWith('_add') &&
        !cr.field_name.endsWith('_delete')
    );
  };

  const columns = [
    {
      title: 'MSIC Codes',
      dataIndex: 'code',
      key: 'code',
      width: '30%',
      render: (code: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const codeChangeRequest = getFieldChangeRequest(record.originalIndex, 'code', record.id);
        
        if (canEdit && !record.isDeleted) {
          let displayValue = code || '';
          // Priority: pending change > change request > add request > record value
          if (getMsicCodeDisplayValue) {
            // Use actualIndex if available (for items in msicCodes array), otherwise use originalIndex
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getMsicCodeDisplayValue(indexToUse, 'code');
            if (pending !== '') {
              displayValue = pending;
            }
          }
          if (codeChangeRequest && codeChangeRequest.new_value) {
            displayValue = codeChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              const newMsic = JSON.parse(record.addRequest.new_value || '{}');
              displayValue = newMsic.code || '';
            } catch (e) {
              displayValue = code || '';
            }
          }
          const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
          return (
          <Input
              id={`msic-code-${indexToUse}`}
              name={`msic-code-${indexToUse}`}
              value={displayValue}
            placeholder="Enter MSIC code"
              onChange={(e) => {
                const val = e.target.value;
                // Use actualIndex if available (for items in msicCodes array), otherwise use originalIndex
                if (handleMsicCodeFieldChange) {
                  handleMsicCodeFieldChange(indexToUse, 'code', val);
                } else {
                  updateMsicCode(indexToUse, 'code', val);
                }
              }}
            />
          );
        }

        if (codeChangeRequest) {
          if (codeChangeRequest.status === 'approved') {
            return <span>{codeChangeRequest.new_value || code || '-'}</span>;
          }
          if (codeChangeRequest.status === 'rejected') {
            return <span>{codeChangeRequest.old_value || code || '-'}</span>;
          }
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {codeChangeRequest.old_value || code || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {codeChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }

        return <span style={cellStyle}>{code || '-'}</span>;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '50%',
      render: (description: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const descriptionChangeRequest = getFieldChangeRequest(record.originalIndex, 'description', record.id);
        
        if (canEdit && !record.isDeleted) {
          let displayValue = description || '';
          if (descriptionChangeRequest && descriptionChangeRequest.new_value) {
            displayValue = descriptionChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              const newMsic = JSON.parse(record.addRequest.new_value || '{}');
              displayValue = newMsic.description || '';
            } catch (e) {
              displayValue = description || '';
            }
          } else if (getMsicCodeDisplayValue) {
            // Use actualIndex if available (for items in msicCodes array), otherwise use originalIndex
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getMsicCodeDisplayValue(indexToUse, 'description');
            if (pending !== '') displayValue = pending;
          }
          const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
          return (
      <Input
              id={`msic-description-${indexToUse}`}
              name={`msic-description-${indexToUse}`}
              value={displayValue}
            placeholder="Enter description"
              onChange={(e) => {
                const val = e.target.value;
                // Use actualIndex if available (for items in msicCodes array), otherwise use originalIndex
                if (handleMsicCodeFieldChange) {
                  handleMsicCodeFieldChange(indexToUse, 'description', val);
                } else {
                  updateMsicCode(indexToUse, 'description', val);
                }
              }}
            />
          );
        }

        if (descriptionChangeRequest) {
          if (descriptionChangeRequest.status === 'approved') {
            return <span>{descriptionChangeRequest.new_value || description || '-'}</span>;
          }
          if (descriptionChangeRequest.status === 'rejected') {
            return <span>{descriptionChangeRequest.old_value || description || '-'}</span>;
          }
  return (
    <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {descriptionChangeRequest.old_value || description || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {descriptionChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }

        return <span style={cellStyle}>{description || '-'}</span>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '20%',
      render: (_: any, record: any) => {
        // Handle delete request
        if (record.isDeleted && record.deleteRequest) {
          return <Tag color="orange">Pending</Tag>;
        }

        // Handle field changes from change requests
        const codeChangeRequest = getFieldChangeRequest(record.originalIndex, 'code', record.id);
        const descriptionChangeRequest = getFieldChangeRequest(record.originalIndex, 'description', record.id);
        
        if ((codeChangeRequest || descriptionChangeRequest) && !record.isDeleted) {
          const hasPending = (codeChangeRequest?.status === 'pending') || 
                           (descriptionChangeRequest?.status === 'pending');
          
          if (hasPending) {
            return <Tag color="orange">Pending</Tag>;
          }
        }

        // Handle new MSIC code from change request (add request)
        if (record.isNewFromRequest && record.addRequest) {
          return <Tag color="orange">Pending</Tag>;
        }

        // Delete button: show for existing rows (with id) and new local rows (without id and not from request)
        if (canEdit && !record.isDeleted && !record.isNewFromRequest) {
          return (
            <Popconfirm
              title="Are you sure you want to delete this MSIC code?"
              onConfirm={() => removeMsicCode(record.originalIndex)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          );
        }

        return null;
      },
    },
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={processedMsicCodes}
        pagination={false}
        size="small"
        onRow={(record: any) => {
          const isDeleted = record.isDeleted;
          const isNew = record.isNewFromRequest;
          const isChanged = hasPendingFieldChange(record.originalIndex);

          // Priority: delete (red) > add (blue) > change (yellow)
          if (isDeleted) {
            return { style: { backgroundColor: '#fff1f0' } };
          }
          if (isNew) {
            return { style: { backgroundColor: '#e6f7ff' } };
          }
          if (isChanged) {
            return { style: { backgroundColor: '#fffbe6' } };
          }
          return {};
        }}
        footer={() => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {canEdit && processedMsicCodes.filter(m => !m.isDeleted && !m.isNewFromRequest).length < maxMsicCodes && (
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => addMsicCode()}
                style={{ color: '#ff4d4f', padding: 0 }}
              >
                (max {maxMsicCodes})
              </Button>
            )}
            {!canEdit && processedMsicCodes.length === 0 && (
              <span style={{ color: '#999' }}>No MSIC codes</span>
            )}
          </div>
        )}
      />

      <div style={{ marginTop: 24, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '12px', width: '30%', fontWeight: 600, backgroundColor: '#fafafa' }}>
                Business Description
              </td>
              <td style={{ padding: '12px', width: '50%' }} colSpan={2}>
                {canEdit ? (
                  <Form.Item name="business_description" style={{ margin: 0 }}>
                    <Input.TextArea
                      id="business-description"
                      rows={4}
                      placeholder="Enter business description"
                    />
                  </Form.Item>
                ) : (
                  (form.getFieldValue('business_description') || '-') as string
                )}
                {(() => {
                  const currentValue = (form.getFieldValue('business_description') || '') as string;
                  const requestedValue = getRequestedValue('business_description');
                  if (requestedValue && String(requestedValue) !== String(currentValue || '')) {
                    return renderRequestedText(String(requestedValue));
                  }
                  return null;
                })()}
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '12px', width: '30%', fontWeight: 600, backgroundColor: '#fafafa' }}>Trademark</td>
              <td style={{ padding: '12px', width: '50%' }}>
                {canEdit ? (
                  <Form.Item name="trademark_logo" style={{ margin: 0 }}>
                    <Select
                      placeholder="Select"
                      options={[
                        { label: 'Yes', value: 'Yes' },
                        { label: 'No', value: 'No' },
                      ]}
                    />
                  </Form.Item>
                ) : (
                  (form.getFieldValue('trademark_logo') || '-') as string
                )}
                {(() => {
                  const currentValue = (form.getFieldValue('trademark_logo') || '') as string;
                  const requestedValue = getRequestedValue('trademark_logo');
                  if (requestedValue && String(requestedValue) !== String(currentValue || '')) {
                    return renderRequestedText(String(requestedValue));
                  }
                  return null;
                })()}
              </td>
              <td style={{ padding: '12px', width: '20%' }}>
                {renderAttachmentCell('trademark_logo')}
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '12px', width: '30%', fontWeight: 600, backgroundColor: '#fafafa' }}>
                Industry Specific License
              </td>
              <td style={{ padding: '12px', width: '50%' }}>
                {canEdit ? (
                  <Form.Item name="industry_specific_license" style={{ margin: 0 }}>
                    <Input
                      id="industry-specific-license"
                      placeholder="Enter date/notes"
                    />
                  </Form.Item>
                ) : (
                  (form.getFieldValue('industry_specific_license') || '-') as string
                )}
                {(() => {
                  const currentValue = (form.getFieldValue('industry_specific_license') || '') as string;
                  const requestedValue = getRequestedValue('industry_specific_license');
                  if (requestedValue && String(requestedValue) !== String(currentValue || '')) {
                    return renderRequestedText(String(requestedValue));
                  }
                  return null;
                })()}
              </td>
              <td style={{ padding: '12px', width: '20%' }}>
                {renderAttachmentCell('industry_specific_license')}
              </td>
            </tr>
          </tbody>
        </table>
        </div>
    </div>
  );
};
